"use client";

import Image from "next/image";
import type { TemplateInfo } from "@/lib/claims/eligibility-types";
import { cn } from "@/lib/utils";

export interface EligibilityStatusProps {
  /** Whether the user is eligible to claim */
  eligible: boolean;
  /** All templates required for this clue */
  required: TemplateInfo[];
  /** Templates the user currently owns */
  owned: TemplateInfo[];
  /** Templates the user is missing */
  missing: TemplateInfo[];
  /** Logic type (AND/OR) */
  logicType: "AND" | "OR";
  /** Whether eligibility is being checked */
  isLoading: boolean;
  /** Optional className for styling */
  className?: string;
}

/**
 * EligibilityStatus Component
 *
 * Displays the user's eligibility status for claiming an NFT reward.
 * Shows progress, owned NFTs, missing NFTs, and guidance messages.
 *
 * Features:
 * - Progress bar showing X/Y required NFTs
 * - List of missing NFTs with names and thumbnails
 * - Checkmarks next to owned prerequisites
 * - Loading state while checking eligibility
 * - Success message when eligible
 */
export function EligibilityStatus({
  eligible,
  required,
  owned,
  missing,
  logicType,
  isLoading,
  className,
}: EligibilityStatusProps) {
  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="eligibility-status"
        className={cn(
          "rounded-lg border border-gray-200 bg-white p-4",
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <span className="text-sm text-gray-600">
            Checking eligibility requirements...
          </span>
        </div>
      </div>
    );
  }

  // No prerequisites - don't show anything
  if (required.length === 0) {
    return null;
  }

  const progress = owned.length;
  const total = required.length;
  const progressPercent = Math.round((progress / total) * 100);

  // Eligible state
  if (eligible) {
    return (
      <div
        data-testid="eligibility-status"
        className={cn(
          "rounded-lg border border-green-200 bg-green-50 p-4",
          className
        )}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
              <svg
                className="h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-green-800">
              You&apos;re eligible to claim!
            </h4>
            <p className="mt-1 text-sm text-green-700">
              You have {progress}/{total} required NFTs. You can claim this reward now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not eligible - show detailed status
  return (
    <div
      data-testid="eligibility-status"
      className={cn(
        "rounded-lg border border-amber-200 bg-amber-50 p-4",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            className="h-5 w-5 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-amber-800">
            Prerequisites Required
          </h4>
          <p className="mt-1 text-sm text-amber-700">
            {logicType === "AND"
              ? `You need to collect all ${total} required NFTs before claiming this reward.`
              : `You need to collect any one of the ${total} required NFTs before claiming this reward.`}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-amber-800">
            {progress}/{total} Required NFTs
          </span>
          <span className="text-amber-600">{progressPercent}%</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-amber-200">
          <div
            className="h-full rounded-full bg-amber-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Owned NFTs */}
      {owned.length > 0 && (
        <div className="mt-4">
          <h5 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Owned (Completed)
          </h5>
          <div className="mt-2 space-y-2">
            {owned.map((template) => (
              <div
                key={template.templateId}
                data-testid="owned-template"
                className="flex items-center gap-3 rounded-md bg-white/60 p-2"
              >
                <div
                  data-testid="checkmark-icon"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500"
                >
                  <svg
                    className="h-3 w-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                {template.imageUrl ? (
                  <Image
                    src={template.imageUrl}
                    alt={template.name}
                    width={32}
                    height={32}
                    className="rounded object-cover"
                    unoptimized
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200 text-xs text-gray-400">
                    NFT
                  </div>
                )}
                <span className="text-sm text-green-800 line-through opacity-75">
                  {template.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing NFTs */}
      {missing.length > 0 && (
        <div className="mt-4">
          <h5 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Still Needed
          </h5>
          <div className="mt-2 space-y-2">
            {missing.map((template) => (
              <div
                key={template.templateId}
                data-testid="missing-template"
                className="flex items-center gap-3 rounded-md bg-white/60 p-2"
              >
                <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-300">
                  {/* Empty circle for missing */}
                </div>
                {template.imageUrl ? (
                  <Image
                    src={template.imageUrl}
                    alt={template.name}
                    width={32}
                    height={32}
                    className="rounded object-cover"
                    unoptimized
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-200 text-xs text-gray-400">
                    NFT
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-amber-800">
                    {template.name}
                  </span>
                  <p className="text-xs text-amber-600 truncate">
                    {template.collectionName}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guidance Message */}
      <div className="mt-4 rounded-md bg-white/60 p-3">
        <p className="text-sm text-amber-800">
          <strong>How to unlock:</strong>{" "}
          {logicType === "AND"
            ? "Visit the other locations in this hunt and collect the required NFTs. You need all of them to unlock this reward."
            : "Visit one of the other locations in this hunt and collect any of the required NFTs to unlock this reward."}
        </p>
      </div>
    </div>
  );
}
